import React from 'react';
import { UI_TEXT } from '../../config/ui-text';

interface SurveyAccessGateProps {
  /** Backend-provided message; falls back to the default US-only copy. */
  message?: string;
  /** Re-run the access check (e.g. after the respondent disables a VPN). */
  onRetry: () => void;
  /** True while the retry request is in flight. */
  retrying?: boolean;
}

/**
 * Soft gate shown when a respondent's IP geolocates outside the allowed region.
 * Unlike SurveyTermination this is non-terminal: the respondent can disable a VPN
 * and retry. Mirrors SurveyTermination's centered-card layout for visual consistency.
 */
export const SurveyAccessGate: React.FC<SurveyAccessGateProps> = ({
  message,
  onRetry,
  retrying = false,
}) => {
  const copy = UI_TEXT.AI_SURVEY.FRAUD_GATE.GEO;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6 flex justify-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-4">{copy.TITLE}</h2>
        <p className="text-gray-600 mb-6 leading-relaxed">{message || copy.MESSAGE}</p>

        <button
          onClick={onRetry}
          disabled={retrying}
          className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {retrying ? copy.CHECKING : copy.RETRY_BUTTON}
        </button>
      </div>
    </div>
  );
};
